<template>
  <div class="saddle-filters">
    <h2>{{ t('common.properties') }}</h2>

    <BfFlex direction="column">
      <CommonFilterAccordion
        :title="t('setup.saddle.bike')"
        icon="/icons/filters/bike.svg"
      >
        <CommonCheckFilters
          :filters="saddleTypeFilters"
          @updated="search($event)"
          @summary="typeSummary = $event"
        />
      </CommonFilterAccordion>

      <CommonFilterAccordion
        :title="t('setup.saddle.width')"
        icon="/icons/filters/saddle__width.svg"
      >
        <CommonSliderFilter
          :unit="t('common.unit.mm')"
          :summary-prefix="t('setup.saddle.width')"
          :filter="saddleWithFilter"
          @updated="search($event)"
          @summary="widthSummary = $event"
        />
      </CommonFilterAccordion>

      <CommonFilterAccordion
        :title="t('setup.saddle.shape.index')"
        icon="/icons/filters/saddle-shape__flat.svg"
      >
        <CommonCheckFilters
          :filters="saddleShapeFilters"
          @updated="search($event)"
          @summary="shapeSummary = $event"
        />
      </CommonFilterAccordion>

      <CommonFilterAccordion
        :title="t('setup.saddle.anatomicalFit')"
        icon="/icons/filters/saddle-anatomical__yes.svg"
      >
        <CommonRadioFilters
          :filters="anatomicalFitFilters"
          @updated="search($event)"
          @summary="anatomicalFitSummary = $event"
        />
      </CommonFilterAccordion>
    </BfFlex>

    <FilterSummary>
      <FilterSummaryItem v-if="typeSummary">{{
        typeSummary
      }}</FilterSummaryItem>
      <FilterSummaryItem v-if="widthSummary">{{
        widthSummary
      }}</FilterSummaryItem>
      <FilterSummaryItem v-if="shapeSummary">{{
        shapeSummary
      }}</FilterSummaryItem>
      <FilterSummaryItem v-if="anatomicalFitSummary">{{
        anatomicalFitSummary
      }}</FilterSummaryItem>
    </FilterSummary>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { BfFlex } from '@finetic/bikefitting-ui'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/store/userStore'
import { useSaddleStore } from '@/store/saddleStore'
import FilterSummary from '@/components/setup/FilterSummary.vue'
import FilterSummaryItem from '@/components/setup/FilterSummaryItem.vue'
import CommonFilterAccordion from '@/components/setup/common/CommonFilterAccordion.vue'
import CommonCheckFilters from '@/components/setup/common/CommonCheckFilters.vue'
import CommonSliderFilter from '@/components/setup/common/CommonSliderFilter.vue'
import CommonRadioFilters from '@/components/setup/common/CommonRadioFilters.vue'
import type {
  CommonCheckFilterType,
  CommonSliderFilterType,
  CommonRadioFilterType,
} from '@/types/filter'

const { t } = useI18n()

const userStore = useUserStore()
const { sessionId } = storeToRefs(userStore)

const saddleStore = useSaddleStore()
const { filters } = storeToRefs(saddleStore)

const typeSummary = ref()
const widthSummary = ref()
const shapeSummary = ref()
const anatomicalFitSummary = ref()

const loading = ref(false)

const saddleTypeFilters: CommonCheckFilterType = {
  TypeRoad: {
    label: t('common.bike.type.road'),
    icons: {
      default: '/icons/filters/bike__road.svg',
      selected: '/icons/filters/bike__road--selected.svg',
    },
  },
  TypeGravel: {
    label: t('common.bike.type.gravel'),
    icons: {
      default: '/icons/filters/bike__gravel.svg',
      selected: '/icons/filters/bike__gravel--selected.svg',
    },
  },
  TypeCyclocross: {
    label: t('common.bike.type.cyclocross'),
    icons: {
      default: '/icons/filters/bike__cyclocross.svg',
      selected: '/icons/filters/bike__cyclocross--selected.svg',
    },
  },
  TypeTimeTrial: {
    label: t('common.bike.type.timeTrial'),
    icons: {
      default: '/icons/filters/bike__time-trial.svg',
      selected: '/icons/filters/bike__time-trial--selected.svg',
    },
  },
  TypeFullTriathlon: {
    label: t('common.bike.type.fullTriathlon'),
    icons: {
      default: '/icons/filters/bike__trifull.svg',
      selected: '/icons/filters/bike__trifull--selected.svg',
    },
  },
  TypeShortTriathlon: {
    label: t('common.bike.type.shortTriathlon'),
    icons: {
      default: '/icons/filters/bike__trishort.svg',
      selected: '/icons/filters/bike__trishort--selected.svg',
    },
  },
  TypeMtbCrossCountry: {
    label: t('common.bike.type.mtbCrossCountry'),
    icons: {
      default: '/icons/filters/bike__mtb-cx.svg',
      selected: '/icons/filters/bike__mtb-cx--selected.svg',
    },
  },
  TypeMtbTrail: {
    label: t('common.bike.type.mtbTrail'),
    icons: {
      default: '/icons/filters/bike__mtb-trial.svg',
      selected: '/icons/filters/bike__mtb-trial--selected.svg',
    },
  },
  TypeTrekking: {
    label: t('common.bike.type.trekking'),
    icons: {
      default: '/icons/filters/bike__trekking.svg',
      selected: '/icons/filters/bike__trekking--selected.svg',
    },
  },
  TypeCity: {
    label: t('common.bike.type.city'),
    icons: {
      default: '/icons/filters/bike__city.svg',
      selected: '/icons/filters/bike__city--selected.svg',
    },
  },
}

const saddleShapeFilters: CommonCheckFilterType = {
  ShapeFlat: {
    label: t('setup.saddle.shape.flat'),
    icons: {
      default: '/icons/filters/saddle-shape__flat.svg',
      selected: '/icons/filters/saddle-shape__flat--selected.svg',
    },
  },
  ShapeSemiCurved: {
    label: t('setup.saddle.shape.semiCurved'),
    icons: {
      default: '/icons/filters/saddle-shape__semi.svg',
      selected: '/icons/filters/saddle-shape__semi--selected.svg',
    },
  },
  ShapeCurved: {
    label: t('setup.saddle.shape.curved'),
    icons: {
      default: '/icons/filters/saddle-shape__curved.svg',
      selected: '/icons/filters/saddle-shape__curved--selected.svg',
    },
  },
}

const anatomicalFitFilters: CommonRadioFilterType = {
  AnatomicalFit: {
    no: {
      label: t('common.no'),
      summary: t('setup.saddle.nonAnatomicalFit'),
      value: 'false',
      icons: {
        default: '/icons/filters/saddle-anatomical__no.svg',
        selected: '/icons/filters/saddle-anatomical__no--selected.svg',
      },
    },
    yes: {
      label: t('common.yes'),
      summary: t('setup.saddle.anatomicalFit'),
      value: 'true',
      icons: {
        default: '/icons/filters/saddle-anatomical__yes.svg',
        selected: '/icons/filters/saddle-anatomical__yes--selected.svg',
      },
    },
  },
}

const saddleWithFilter: CommonSliderFilterType = {
  value: {
    Width: filters.value.Width ?? 100,
  },
  min: {
    MinWidth: 100,
  },
  max: {
    MaxWidth: 250,
  },
}

const search = (e?: Record<string, any>) => {
  saddleStore.reset()

  if (e) saddleStore.setFilters(e)

  saddleStore.retrieveSaddles(sessionId?.value ?? '').then(() => {
    loading.value = false
  })
}
</script>

<style lang="scss">
.saddle-filters {
  padding: 2.4rem;
}
</style>
